import AddIcon from '@mui/icons-material/Add';
import { Box, Fab, Slide, SlideProps, Snackbar, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/he';
import minMax from 'dayjs/plugin/minMax';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';

import { BaseAccidentV2, PolicyV2 } from '../../../backendsdk';
import { useAlert } from '../../../hooks/alert';
import useApi from '../../../hooks/api';
import useIsMobile from '../../../hooks/isMobile';
import useProfile from '../../../hooks/profile';
import { useQuery } from '../../../hooks/query';
import { accidentsPageDefs } from '../../../utils/Pages';
import { getStorageItem, setStorageItem } from '../../../utils/Storage';
import { formatDateFromOrdinal, formatDateTime } from '../../../utils/TimeFormatter';
import { DEFAULT_TITLE } from '../../Layout';
import AccidentDetailsComponent from './AccidentDetails';
import AccidentGrid from './AccidentV1/AccidentGrid';
import { AccidentStages } from './AccidentV1/AccidentStage';
import { AccidentToolbar } from './AccidentV1/AccidentToolbar';
import { AccidentFilterField, SortField, SortOrder } from './Defs';

dayjs.extend(minMax);

export interface AccidentFilters {
    subFleet: string;
    damageCause: string[];
    speed: string[];
    fault: string[];
    pointOfImpact: string[];
}

export const FILTER_KEY_TO_QUERY_PARAM: Record<keyof AccidentFilters, string> = {
    subFleet: 'sub_fleet',
    damageCause: 'damage_cause',
    speed: 'speed',
    fault: 'fault',
    pointOfImpact: 'point_of_impact',
};

interface AccidentComponentProps {
    handlePageVersionChange: CallableFunction;
}

const AccidentComponent: React.FC<AccidentComponentProps> = (props) => {
    const [accidents, setAccidents] = useState<Array<BaseAccidentV2>>([]);
    const [policies, setPolicies] = useState<Array<PolicyV2>>([]);
    const [loadingPolicies, setLoadingPolicies] = useState<boolean>(true);
    const [loadingCounter, setLoadingCounter] = useState<number>(0);
    const [isLoadingAccidents, setIsLoadingAccidents] = useState<boolean>(true);
    const [isLoadingModal, setIsLoadingModal] = useState<boolean>(false);
    const [handlingStatusList, setHandlingStatusList] = useState<Array<string>>([]);
    const initialDay = dayjs().date() < 16 ? 1 : 16;
    const [filterField, setFilterField] = useState<AccidentFilterField>(
        (getStorageItem('OE-filter-field') as AccidentFilterField) || 'timestamp',
    );
    const query = useQuery();
    let initialFromDate = dayjs().startOf('day').subtract(3, 'month').set('date', initialDay);
    if (query.has('from')) {
        initialFromDate = dayjs.unix(parseInt(query.get('from') as string));
    }
    const [fromDate, setFromDate] = useState<Dayjs>(initialFromDate);
    let initialToDate = dayjs().endOf('day');
    if (query.has('to')) {
        initialToDate = dayjs.min(initialToDate, dayjs.unix(parseInt(query.get('to') as string)));
    }
    const [toDate, setToDate] = useState<Dayjs>(initialToDate);
    const history = useHistory();
    const { accidentId } = useParams<{ accidentId: string }>();
    const { t } = useTranslation();
    const { agencyApi } = useApi();
    const { profile } = useProfile();
    const [alertElement, setAlert] = useAlert();
    const [currentStage, setCurrentStage] = useState<string>('all');
    const [searchText, setSearchText] = useState<string>('');
    const [sort, setSort] = useState<[SortField, SortOrder]>(['notification', 'desc']);
    const parseQueryParam = (param: string) => query.get(param)?.split(',') || [];
    const ALL_FLEET = t('content.metrics.all_fleet');
    const initialFilters: AccidentFilters = {
        subFleet: !!profile.sub_fleet ? profile.sub_fleet : query.get(FILTER_KEY_TO_QUERY_PARAM.subFleet) || ALL_FLEET,
        damageCause: parseQueryParam(FILTER_KEY_TO_QUERY_PARAM.damageCause),
        speed: parseQueryParam(FILTER_KEY_TO_QUERY_PARAM.speed),
        fault: parseQueryParam(FILTER_KEY_TO_QUERY_PARAM.fault),
        pointOfImpact: parseQueryParam(FILTER_KEY_TO_QUERY_PARAM.pointOfImpact),
    };
    const [filters, setFilters] = useState<AccidentFilters>(initialFilters);
    const match = useRouteMatch();
    const isMobile = useIsMobile();

    useEffect(() => {
        document.title = `${DEFAULT_TITLE} | ${t(`navigator.${accidentsPageDefs.name}`)}`;
    }, []);

    useEffect(() => {
        getPolicies();
    }, []);

    useEffect(() => {
        agencyApi
            .agencyV1CustomerDataGet()
            .then((res) =>
                setHandlingStatusList(res.data.handling_status.map((status: { name: string }) => status.name)),
            );
    }, []);

    useEffect(() => {
        const parseQueryParam = (param: string) => {
            const queryParam = query.get(param);
            if (!!queryParam) {
                return decodeURIComponent(queryParam).split(',') || [];
            }
            return [];
        };

        const filters: AccidentFilters = {
            subFleet: query.get(FILTER_KEY_TO_QUERY_PARAM.subFleet) || ALL_FLEET,
            damageCause: parseQueryParam(FILTER_KEY_TO_QUERY_PARAM.damageCause),
            speed: parseQueryParam(FILTER_KEY_TO_QUERY_PARAM.speed),
            fault: parseQueryParam(FILTER_KEY_TO_QUERY_PARAM.fault),
            pointOfImpact: parseQueryParam(FILTER_KEY_TO_QUERY_PARAM.pointOfImpact),
        };

        setFilters(filters);

        if (query.has('license_plate')) {
            setSearchText(query.get('license_plate') as string);
            query.delete('license_plate');
            history.replace(`${match.url}?${query.toString()}`);
        }
        if (query.has('driver_name')) {
            setSearchText(query.get('driver_name') as string);
            query.delete('driver_name');
            history.replace(`${match.url}?${query.toString()}`);
        }
    }, [query]);

    const resetState = () => {
        setLoadingCounter((prev) => prev + 1);
    };

    const getPolicies = () => {
        agencyApi
            .agencyV2PolicyGet()
            .then((res) => {
                setPolicies(res.data);
            })
            .catch((err) => {
                if (err.response?.status !== 404) {
                    setAlert({ message: t('content.accidents.error_loading'), type: 'error', duration: 6000 });
                }
            })
            .finally(() => setLoadingPolicies(false));
    };

    const loadAccidents = (
        controller: AbortController,
        filterBy: AccidentFilterField,
        from: Dayjs,
        to: Dayjs,
        last_id?: number,
    ) => {
        setIsLoadingAccidents(true);
        let lastId: number | undefined = undefined;
        if (last_id != undefined) {
            lastId = last_id;
        }
        const filterParams =
            filterBy === 'created_at'
                ? { createdFrom: from.unix(), createdTo: to.unix() }
                : { timeFrom: from.unix(), timeTo: to.unix() };

        agencyApi
            .agencyV2AccidentGet(
                {
                    lastId,
                    limit: 1000,
                    subFleet: filters.subFleet !== ALL_FLEET ? filters.subFleet : undefined,
                    damageCause: filters.damageCause.join(',') || undefined,
                    speed: filters.speed.join(',') || undefined,
                    fault: filters.fault.join(',') || undefined,
                    pointOfImpact: filters.pointOfImpact.join(',') || undefined,
                    ...filterParams,
                },
                { signal: controller.signal },
            )
            .then((res) => {
                if (res.data.length > 0) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const accidents = res.data.map((a: any) => {
                        a.datetime = formatDateTime(a.datetime).replace(' ', 'T');
                        a.visiting_scene_date = a.visiting_scene_date
                            ? formatDateFromOrdinal(a.visiting_scene_date)
                            : '';
                        return a;
                    });
                    setAccidents((prevAccidents) => {
                        const seen = new Set();
                        const allAccidents = [...accidents, ...prevAccidents];
                        const newAccidents = allAccidents.reduce((obj: Array<BaseAccidentV2>, elem: BaseAccidentV2) => {
                            if (seen.has(elem.accident_id)) {
                                return obj;
                            }
                            seen.add(elem.accident_id);
                            return [...obj, elem];
                        }, []);
                        return newAccidents.sort(
                            (a: BaseAccidentV2, b: BaseAccidentV2) => b.accident_id - a.accident_id,
                        );
                    });
                    loadAccidents(controller, filterBy, from, to, res.data[res.data.length - 1].accident_id);
                } else {
                    setIsLoadingAccidents(false);
                }
            })
            .catch(() => {
                if (!controller?.signal.aborted) {
                    setIsLoadingAccidents(false);
                    setAlert({ message: t('content.accidents.error_loading'), type: 'error', duration: 6000 });
                }
            });
    };

    useEffect(() => {
        setStorageItem('OE-filter-field', filterField);
    }, [filterField]);

    useEffect(() => {
        if (fromDate.isValid() && toDate.isValid() && fromDate.isBefore(toDate)) {
            const controller = new AbortController();
            loadAccidents(controller, filterField, fromDate, toDate);
            return () => controller.abort();
        }
    }, [loadingCounter, filterField, fromDate, toDate, filters]);

    useEffect(() => setLoadingCounter((prev) => prev + 1), []);

    const filterAccidents = (elem: BaseAccidentV2) => {
        const lowerFilterValues = searchText
            .toLocaleLowerCase()
            .split(',')
            .map((value) => value.trim());
        return lowerFilterValues.some((lowerFilterValue) => {
            return Boolean(
                elem.accident_id.toString().toLocaleLowerCase().includes(lowerFilterValue) ||
                    elem.description?.toLocaleLowerCase().includes(lowerFilterValue) ||
                    elem.internal_status?.toLocaleLowerCase().includes(lowerFilterValue) ||
                    elem.internal_id.toLocaleLowerCase().includes(lowerFilterValue) ||
                    elem.license_plate
                        .toLocaleLowerCase()
                        .replaceAll('-', '')
                        .includes(lowerFilterValue.replaceAll('-', '')) ||
                    elem.driver_name?.toLocaleLowerCase().includes(lowerFilterValue) ||
                    elem.sub_fleet_name?.toLocaleLowerCase().includes(lowerFilterValue) ||
                    elem.assignee?.toLocaleLowerCase().includes(lowerFilterValue) ||
                    dayjs.unix(elem.created_at).format(profile.dateFormat).includes(lowerFilterValue) ||
                    dayjs.unix(elem.timestamp).format(profile.dateFormat).includes(lowerFilterValue),
            );
        });
    };

    const moveAccident = (accidentId: number, stage: string) => {
        setAccidents((prev) => {
            return prev.map((accident) =>
                accident.accident_id == accidentId ? { ...accident, stage: stage } : accident,
            );
        });
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const changeAccidentStage = (accidentId: number, newStage: string) => {
        const accident = accidents.find((accident) => accident.accident_id == accidentId);
        if (accident != undefined) {
            moveAccident(accidentId, newStage);
            return agencyApi
                .agencyV1AccidentAccidentIdPatch({
                    accidentId: accidentId,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    extendedAccident: { ...(accident as any), stage: newStage },
                })
                .then(() => {
                    setAlert({
                        message: t('content.accidents.change_stage_success', {
                            stage: t(`content.accidents.stage.${profile.customer.settings.country}.${newStage}`),
                        }),
                        duration: 6000,
                        type: 'success',
                    });
                })
                .catch((res) => {
                    moveAccident(accidentId, accident.stage);
                    setAlert({
                        message: t(`content.accidents.${res.response.data.message || 'change_stage_error'}`),
                        duration: 6000,
                        type: 'error',
                    });
                });
        }
        return Promise.resolve();
    };

    const accidentsWithinDateRange = accidents.filter((accident) => {
        const datetimeField = filterField == 'timestamp' ? accident.timestamp : accident.created_at;
        return accident[filterField] >= fromDate.unix() && datetimeField <= toDate.unix();
    });
    const filteredAccidents = accidentsWithinDateRange.filter(filterAccidents).sort((a, b) => {
        const [field, order] = sort;
        const aField = a[field];
        const bField = b[field];
        if (aField === null) {
            if (bField === null) {
                return order === 'asc' ? a.accident_id - b.accident_id : b.accident_id - a.accident_id;
            }
            return order === 'asc' ? -1 : 1;
        }
        if (bField === null) {
            return order === 'asc' ? 1 : -1;
        }
        if (aField < bField) {
            return order === 'asc' ? -1 : 1;
        }
        if (aField > bField) {
            return order === 'asc' ? 1 : -1;
        }
        return 0;
    });

    const stageAccidents: Record<string, BaseAccidentV2[]> = filteredAccidents.reduce(
        (acc: Record<string, BaseAccidentV2[]>, accident: BaseAccidentV2) => {
            const stage = accident.stage.toLocaleLowerCase();
            if (acc[stage] == undefined) {
                acc[stage] = [];
            }
            acc[stage].push(accident);
            return acc;
        },
        {},
    );

    const stageAccidentsList = currentStage === 'all' ? filteredAccidents : stageAccidents[currentStage] || [];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
            {alertElement}
            <LoadingSnackbar isLoading={isLoadingAccidents} />
            <AccidentToolbar
                handlePageVersionChange={props.handlePageVersionChange}
                searchText={searchText}
                setSearchText={setSearchText}
                filterField={filterField}
                setFilterField={setFilterField}
                fromDate={fromDate}
                setFromDate={setFromDate}
                toDate={toDate}
                setToDate={setToDate}
                resetState={resetState}
                sort={sort}
                setSort={setSort}
                setAlert={setAlert}
                stageAccidents={stageAccidents}
                currentStage={currentStage}
                onStageClick={(stage: string) => setCurrentStage(stage)}
                filters={filters}
                setFilters={setFilters}
                setAccidents={setAccidents}
            />
            {isLoadingModal ? (
                <div
                    style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'var(--bg-color)',
                        opacity: 0.5,
                        zIndex: 1051,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <div style={{ transform: 'scale(2)' }}>
                        <CircularProgress />
                    </div>
                </div>
            ) : null}
            <Box sx={{ display: 'flex', height: '100%', width: '100%', minHeight: 0 }}>
                {!isMobile ? (
                    <AccidentStages
                        stageAccidents={stageAccidents}
                        currentStage={currentStage}
                        onStageClick={(stage: string) => setCurrentStage(stage)}
                    />
                ) : null}
                <AccidentGrid
                    accidents={stageAccidentsList}
                    accidentId={parseInt(accidentId)}
                    policies={policies}
                    setAccidents={setAccidents}
                    isLoading={isLoadingAccidents}
                    setAlert={setAlert}
                />
                {accidentId ? (
                    <AccidentDetailsComponent
                        pageVersion="v1"
                        accidentId={accidentId}
                        refreshTable={setAccidents}
                        changeAccidentStage={changeAccidentStage}
                        setIsLoadingModal={setIsLoadingModal}
                        handlingStatusList={handlingStatusList}
                        setHandlingStatusList={setHandlingStatusList}
                        onClose={() => history.push(`/accidents?${query.toString()}`)}
                        policies={policies}
                        loadingPolicies={loadingPolicies}
                    />
                ) : null}
                {isMobile ? (
                    <Fab
                        id="btn-new"
                        color="secondary"
                        aria-label="add"
                        onClick={() => history.push(`/accidents/0?${query.toString()}`)}
                        sx={{ position: 'absolute', bottom: 15, right: 15 }}
                    >
                        <AddIcon />
                    </Fab>
                ) : null}
            </Box>
        </Box>
    );
};

export default AccidentComponent;

function SlideTransition(props: SlideProps) {
    return <Slide {...props} direction="up" />;
}

const LoadingSnackbar: React.FC<{ isLoading: boolean }> = React.memo(function LoadingSnackbar(props: {
    isLoading: boolean;
}) {
    const { t } = useTranslation();

    return (
        <Snackbar
            open={props.isLoading}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            TransitionComponent={SlideTransition}
            message={<Typography>{t('content.accidents.loading')}</Typography>}
            sx={{ '& .MuiPaper-root': { backgroundColor: 'primary.main', minWidth: 0 }, zIndex: 1200 }}
        />
    );
});
